import {
  getSurfaceBlock,
  SurfaceBlockModel,
} from '@blocksuite/affine/blocks/surface';
import { Bound } from '@blocksuite/affine/global/gfx';
import type { BlockStdScope } from '@blocksuite/affine/std';
import {
  GfxBlockElementModel,
  type GfxModel,
} from '@blocksuite/affine/std/gfx';
import {
  type CanvasPlacementRect,
  createRightSideBranchCandidates,
  findFreeCanvasRect,
} from '@streamlain/database-identity';

import {
  type AIChatBlockModel,
  CHAT_BLOCK_HEIGHT,
  CHAT_BLOCK_WIDTH,
} from '../blocks';

const CHILD_CHAT_HORIZONTAL_GAP = 120;
const CHILD_CHAT_VERTICAL_GAP = 60;
const CHILD_CHAT_COLLISION_GAP = 40;

/**
 * Places a child to the right of its parent, centred on the parent. Occupied
 * slots are skipped in the order centre, below, above, then farther out.
 * @param parentModel - The model of the parent block.
 * @param std - The editor scope used to inspect all canvas objects.
 * @returns The calculated bounding box for the child block.
 */
export function calcChildBound(
  parentModel: AIChatBlockModel,
  std: BlockStdScope
) {
  const surface = getSurfaceBlock(std.store);
  const parentXYWH = Bound.deserialize(parentModel.xywh);
  const parentRect: CanvasPlacementRect = [
    parentXYWH.x,
    parentXYWH.y,
    parentXYWH.w,
    parentXYWH.h,
  ];
  const occupied = collectOccupiedCanvasRects(std, surface);
  const [x, y, width, height] = findFreeCanvasRect({
    occupied,
    width: CHAT_BLOCK_WIDTH,
    height: CHAT_BLOCK_HEIGHT,
    gap: CHILD_CHAT_COLLISION_GAP,
    preferredCandidates: createRightSideBranchCandidates({
      anchor: parentRect,
      width: CHAT_BLOCK_WIDTH,
      height: CHAT_BLOCK_HEIGHT,
      gapX: CHILD_CHAT_HORIZONTAL_GAP,
      gapY: CHILD_CHAT_VERTICAL_GAP,
    }),
  });

  return new Bound(x, y, width, height);
}

function collectOccupiedCanvasRects(
  std: BlockStdScope,
  surface: SurfaceBlockModel | null
): CanvasPlacementRect[] {
  if (!surface) return [];

  const blockModels = std.store
    .getAllModels()
    .filter(
      model =>
        model instanceof GfxBlockElementModel &&
        (model.parent instanceof SurfaceBlockModel ||
          model.parent?.role === 'root')
    ) as GfxModel[];
  const elements = blockModels.concat(
    surface.elementModels.filter(element => element.type !== 'connector')
  );

  return elements.flatMap(element => {
    try {
      const bound = Bound.deserialize(element.xywh);
      if (
        ![bound.x, bound.y, bound.w, bound.h].every(Number.isFinite) ||
        bound.w <= 0 ||
        bound.h <= 0
      ) {
        return [];
      }
      return [[bound.x, bound.y, bound.w, bound.h] as CanvasPlacementRect];
    } catch {
      return [];
    }
  });
}
